import { ncFileStatuses } from '@helpers';
import { TimeCategory } from '@interfaces/base';
import type { ProductionBatchStatistics } from '@interfaces/search';

export interface FilterTimeCategoriesDataHelperParams<T> {
  data: T[];
  batch?: ProductionBatchStatistics | null;
  isWithERP?: boolean | null;
  ncFileStatus?: (typeof ncFileStatuses)[keyof typeof ncFileStatuses];
  skipItems?: number;
  allowedTimeCategories?: TimeCategory[];
}

export const filterTimeCategoriesData = <T extends { name: TimeCategory }>({
  data,
  batch,
  isWithERP,
  ncFileStatus: ncFileStatusFromParams,
  allowedTimeCategories,
  skipItems = 0,
}: FilterTimeCategoriesDataHelperParams<T>): T[] => {
  const hasScheduledData = !!(
    batch?.scheduledSetupTimeInSeconds ||
    batch?.scheduledActiveTimeTotalInSeconds ||
    batch?.scheduledChangeTimeTotalInSeconds ||
    batch?.scheduledTotalTimeTotalInSeconds
  );

  const defaultNCFileStatus = batch?.ncFileStatus;
  const ncFileStatus = ncFileStatusFromParams ?? defaultNCFileStatus;

  const ncFileLinked = ncFileStatus === ncFileStatuses.Linked;
  const ncFileIgnored = ncFileStatus === ncFileStatuses.Ignored;
  const ncFileSetup = ncFileStatus === ncFileStatuses.Setup;

  const batchLinked = !!(batch?.links && batch.links?.length > 0);

  if (allowedTimeCategories) {
    return data.filter((item) => allowedTimeCategories.includes(item.name));
  }

  if (isWithERP && batchLinked) {
    return data;
  }

  return data.filter((item, index) => {
    if (index < skipItems) {
      return true;
    }

    if (ncFileIgnored || ncFileSetup) {
      return item.name === TimeCategory.ACTUAL;
    }

    if (item.name === TimeCategory.SCHEDULED && hasScheduledData) {
      return true;
    }

    if (!isWithERP && batchLinked) {
      return item.name !== TimeCategory.SCHEDULED;
    }

    if (isWithERP && !batchLinked && ncFileLinked) {
      return item.name !== TimeCategory.PAID;
    }

    if (!batchLinked && !ncFileLinked) {
      return [TimeCategory.ACTUAL, TimeCategory.IDEAL].includes(item.name);
    }

    return true;
  });
};
